import type { ContentVersion } from "../utils/types"

type SyncState = "synced" | "syncing" | "error"

interface PendingSync {
  id: string
  points: number
  timestamp: number
  activityKind: string
  challengeId: string
}

export const useContentStore = defineStore("content", {
  state: (): {
    versions: Record<string, ContentVersion | undefined>
    currentVersionId: string | null
    syncState: SyncState
    pendingSyncs: PendingSync[]
    lastSyncTime: number | null
    syncTimeoutMs: number
  } => ({
    versions: {},
    currentVersionId: null,
    syncState: "synced",
    pendingSyncs: [],
    lastSyncTime: null,
    syncTimeoutMs: 5000 // 5 seconds default timeout
  }),
  actions: {
    setContentVersion(
      contentVersionId: string,
      contentVersion: ContentVersion
    ): void {
      this.versions[contentVersionId] = contentVersion
      this.currentVersionId = contentVersionId
    },
    addPendingSync(sync: PendingSync): void {
      this.pendingSyncs.push(sync)
      this.syncState = "syncing"
    },
    removePendingSync(syncId: string): void {
      this.pendingSyncs = this.pendingSyncs.filter((s) => s.id !== syncId)
      if (this.pendingSyncs.length === 0) {
        this.syncState = "synced"
        this.lastSyncTime = Date.now()
      }
    },
    markSyncError(): void {
      this.syncState = "error"
    },
    clearSyncError(): void {
      if (this.pendingSyncs.length === 0) {
        this.syncState = "synced"
      } else {
        this.syncState = "syncing"
      }
    },
    clearAllPendingSyncs(): void {
      this.pendingSyncs = []
      this.syncState = "synced"
      this.lastSyncTime = Date.now()
    },
    hasTimedOutSyncs(): boolean {
      const now = Date.now()
      return this.pendingSyncs.some(
        (sync) => now - sync.timestamp > this.syncTimeoutMs
      )
    }
  },
  getters: {
    currentVersion(): ContentVersion | undefined {
      if (this.currentVersionId) {
        return this.versions[this.currentVersionId]
      }
      return undefined
    },
    isSyncing(): boolean {
      return this.syncState === "syncing"
    },
    hasError(): boolean {
      return this.syncState === "error"
    },
    pendingSyncCount(): number {
      return this.pendingSyncs.length
    }
  },
  persist: true
})
